import { useQuery } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'
import { apiClient } from '@/api/client'

/**
 * Aging open-orders alert. Surfaces real-value orders (a payable balance or a
 * fired item) that have been open far longer than a normal ticket — a forgotten
 * order someone must pay or cancel, so it's caught in days not months (the
 * Chunkyard Rs 2,530 dine-in sat open 7 weeks). Read-only: it lists them; it
 * never auto-closes a real order. Renders nothing when there are none.
 *
 * `variant="banner"` is the full list (Day Close); `variant="tile"` is a compact
 * count for the dashboard.
 */
export function AgingOpenOrdersAlert({
  hours = 24,
  variant = 'banner',
}: {
  hours?: number
  variant?: 'banner' | 'tile'
}) {
  const { data } = useQuery({
    queryKey: ['aging-open-orders', hours],
    queryFn: () => apiClient.getAgingOpenOrders(hours),
    refetchInterval: 5 * 60 * 1000,
    staleTime: 60 * 1000,
  })

  const res = data?.success ? data.data : null
  if (!res || res.count === 0) return null

  const money = (n: number) => `Rs ${Math.round(n).toLocaleString()}`
  const ageLabel = (h: number) => (h >= 48 ? `${Math.floor(h / 24)}d` : `${Math.round(h)}h`)

  if (variant === 'tile') {
    return (
      <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-800 dark:bg-amber-900/20">
        <div className="flex items-center gap-2 text-amber-900 dark:text-amber-100">
          <AlertTriangle className="h-4 w-4" />
          <span className="text-sm font-semibold">
            {res.count} aging open order{res.count === 1 ? '' : 's'}
          </span>
        </div>
        <p className="mt-1 text-xs text-amber-800 dark:text-amber-200">
          Older than {res.threshold_hours}h · {money(res.total_amount)} unresolved
        </p>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
            {res.count} open order{res.count === 1 ? '' : 's'} older than {res.threshold_hours}h
            {' · '}
            {money(res.total_amount)} unresolved
          </p>
          <p className="mt-0.5 text-xs text-amber-800 dark:text-amber-200">
            Review before closing — pay or cancel each. (Rs 0 empty tickets are cleared
            automatically and aren't listed here.)
          </p>
          <div className="mt-2 max-h-40 overflow-y-auto rounded border border-amber-200 bg-white/60 dark:border-amber-800 dark:bg-black/20">
            <table className="w-full text-left text-xs">
              <thead className="text-amber-700 dark:text-amber-300">
                <tr>
                  <th className="px-2 py-1 font-medium">Order</th>
                  <th className="px-2 py-1 font-medium">Type</th>
                  <th className="px-2 py-1 font-medium">Table</th>
                  <th className="px-2 py-1 font-medium">Age</th>
                  <th className="px-2 py-1 text-right font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {res.orders.map((o) => (
                  <tr key={o.order_number} className="border-t border-amber-100 dark:border-amber-900">
                    <td className="px-2 py-1 font-mono">{o.order_number}</td>
                    <td className="px-2 py-1 capitalize">{o.order_type.replace('_', ' ')}</td>
                    <td className="px-2 py-1">{o.table_label || '—'}</td>
                    <td className="px-2 py-1">{ageLabel(o.age_hours)}</td>
                    <td className="px-2 py-1 text-right">{money(o.total_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
